import { useTranslation } from "react-i18next";
import type { GitlabRollup } from "@/api/types/GitlabRollup";
import { gitlabChip, type GitlabTone } from "@/lib/gitlab";
import { Icon } from "./Icon";
import { cx } from "./ui";

export const GITLAB_TONES: Record<GitlabTone, string> = {
  pass: "bg-pass-bg text-pass-fg",
  fail: "bg-fail-bg text-fail-fg",
  accent: "bg-accent-soft text-accent",
  neutral: "bg-surface-2 text-text-2",
  stale: "bg-stale-bg text-stale-fg",
};

/** A task's linked work at a glance: icon and short text, with the rest in the tooltip. Draws nothing without a state. */
export function GitlabChip({ rollup, className }: { rollup: GitlabRollup | null | undefined; className?: string }) {
  const { t } = useTranslation();
  const chip = gitlabChip(rollup, t);
  if (!chip) return null;
  return (
    <span title={chip.description} className={cx("inline-flex max-w-full min-w-0 items-center gap-1", className)}>
      <span className={cx("inline-flex min-w-0 items-center gap-1.5 rounded-full px-2.5 text-xs leading-5 font-semibold", GITLAB_TONES[chip.tone])}>
        <Icon name={chip.icon} size={13} className="shrink-0" />
        <span aria-hidden="true" className="truncate">
          {chip.label}
        </span>
      </span>
      {chip.alsoChanged && (
        <span aria-hidden="true" className={cx("inline-flex size-5 shrink-0 items-center justify-center rounded-full", GITLAB_TONES.stale)}>
          <Icon name="stale" size={12} />
        </span>
      )}
      <span className="sr-only">{chip.description}</span>
    </span>
  );
}

/** New commits arrived after the last finished round. The round's results stand; only the code moved on. */
export function ChangedSinceTestedChip() {
  const { t } = useTranslation();
  return (
    <span
      title={t("gitlab.chipHint.changed_since_tested")}
      className={cx("inline-flex items-center gap-1.5 rounded-full px-2.5 text-xs leading-5 font-semibold whitespace-nowrap", GITLAB_TONES.stale)}
    >
      <Icon name="stale" size={13} />
      {t("gitlab.chip.changed_since_tested")}
    </span>
  );
}
